// Shared validation constants
// This file should not import from other files to avoid circular dependencies

/**
 * URL format regex: requires http(s)://, a valid domain with at least one dot,
 * and no whitespace. Rejects malformed URLs like "https:/google.com" or "htp://test".
 */
export const URL_REGEX_PATTERN =
  '^https?:\\/\\/[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+(\\/[^\\s]*)?$';
export const URL_REGEX_ERROR_MESSAGE =
  'Please enter a valid URL (e.g. https://example.com). URLs must begin with http:// or https:// followed by a valid domain name.';

export const URL_REGEX = {
  PATTERN: URL_REGEX_PATTERN,
  ERROR_MESSAGE: URL_REGEX_ERROR_MESSAGE,
} as const;

/** Maximum allowed URL length (standard browser/server limit). */
export const URL_MAX_LENGTH = 2048;
export const URL_MAX_LENGTH_ERROR_MESSAGE = `URL cannot exceed ${URL_MAX_LENGTH} characters.`;

/**
 * Patterns matching local/private network hosts that should be blocked for security.
 * Covers localhost, IPv4 loopback, and RFC 1918 private ranges.
 */
const PRIVATE_NETWORK_PATTERNS: RegExp[] = [
  /^https?:\/\/localhost([:/?#]|$)/i,
  /^https?:\/\/127\.\d{1,3}\.\d{1,3}\.\d{1,3}([:/?#]|$)/i,
  /^https?:\/\/10\.\d{1,3}\.\d{1,3}\.\d{1,3}([:/?#]|$)/i,
  /^https?:\/\/172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}([:/?#]|$)/i,
  /^https?:\/\/192\.168\.\d{1,3}\.\d{1,3}([:/?#]|$)/i,
  /^https?:\/\/0\.0\.0\.0([:/?#]|$)/i,
  /^https?:\/\/\[::1\]/i,
];

export const PRIVATE_NETWORK_ERROR_MESSAGE =
  'Local and private network URLs are not allowed for security reasons.';

/**
 * Checks if a URL points to a local or private network address.
 * @param url - The URL string to check
 * @returns true if the URL targets a private/local network
 */
export const isPrivateNetworkUrl = (url: string): boolean =>
  PRIVATE_NETWORK_PATTERNS.some((pattern) => pattern.test(url));

/**
 * Extracts the hostname from a URL string.
 * @param url - The URL to extract the hostname from
 * @returns The hostname or null if parsing fails
 */
const extractHostname = (url: string): string | null => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Checks if a URL belongs to a restricted domain.
 * Matches both exact domains and subdomains (e.g. "m.facebook.com" matches "facebook.com").
 * @param url - The URL to check
 * @param restrictedDomains - Array of domain names to block (e.g. ['facebook.com'])
 * @returns true if the URL's domain is restricted
 */
export const isRestrictedDomain = (
  url: string,
  restrictedDomains: string[]
): boolean => {
  if (restrictedDomains.length === 0) {
    return false;
  }

  const hostname = extractHostname(url);

  if (!hostname) {
    return false;
  }

  return restrictedDomains.some((domain) => {
    const normalizedDomain = domain.toLowerCase();

    return (
      hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`)
    );
  });
};

export const RESTRICTED_DOMAIN_ERROR_MESSAGE =
  'URLs from this domain are not allowed.';

export interface UrlValidationOptions {
  /** Maximum allowed URL length. Defaults to 2048. */
  maxLength?: number;
  /** Whether to block local/private network URLs. Defaults to true. */
  blockPrivateNetworks?: boolean;
  /** List of domain names to block (e.g. ['facebook.com', 'instagram.com']). */
  restrictedDomains?: string[];
}

/**
 * Comprehensive URL validator. Checks format, length, private networks, and restricted domains.
 * @param url - The URL string to validate
 * @param options - Optional validation configuration
 * @returns Error message string if invalid, null if valid
 */
export const validateUrl = (
  url: string,
  options: UrlValidationOptions = {}
): string | null => {
  const {
    maxLength = URL_MAX_LENGTH,
    blockPrivateNetworks = true,
    restrictedDomains = [],
  } = options;

  const trimmed = url.trim();

  if (!trimmed) {
    return null; // Empty check is the caller's responsibility
  }

  // Length check
  if (trimmed.length > maxLength) {
    return URL_MAX_LENGTH_ERROR_MESSAGE;
  }

  // Format check
  if (!new RegExp(URL_REGEX_PATTERN).test(trimmed)) {
    return URL_REGEX_ERROR_MESSAGE;
  }

  // Private network check
  if (blockPrivateNetworks && isPrivateNetworkUrl(trimmed)) {
    return PRIVATE_NETWORK_ERROR_MESSAGE;
  }

  // Restricted domain check
  if (isRestrictedDomain(trimmed, restrictedDomains)) {
    return RESTRICTED_DOMAIN_ERROR_MESSAGE;
  }

  return null;
};

// Numeric validation constants
export const NUMERIC_VALUE_VALIDATION = {
  MAX_VALUE: 999999999999999,
  MAX_CHARS: 15,
  REGEX: '[0-9]*',
  ERROR_MESSAGES: {
    INVALID: 'Please enter a valid number',
    MAX_CHARS_EXCEEDED: 'Value cannot exceed 15 digits',
  },
} as const;
